import { Box } from '@chakra-ui/react';
import VCard from '@voly/ui-components/src/v2/VCard';
import VFormField from '@voly/ui-components/src/v2/VFormField.jsx';
import VSelect from '@voly/ui-components/src/v2/VSelect.jsx';
import VStack from '@voly/ui-components/src/v2/VStack';
import VStackRow from '@voly/ui-components/src/v2/VStackRow';
import VText from '@voly/ui-components/src/v2/VText.jsx';
import VTooltip from '@voly/ui-components/src/v2/VTooltip';
import { ReactElement, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import VBadge from '@voly/ui-components/src/v2/VBadge.jsx';
import VMaterialIcon from '@voly/ui-components/src/v2/icons/VMaterialIcon.jsx';

const CURRENT_SITE_STYLE = { cursor: 'not-allowed', opacity: 0.5, pointerEvents: 'none' } as const;
const SELECTABLE_SITE_STYLE = {
  cursor: 'pointer',
  _hover: { borderColor: 'border.emphasized' },
} as const;

export type TSelectOption = {
  label: string;
  value: string;
};

export type TSiteOption = TSelectOption & {
  accountId: string;
  customFields: TCustomField[];
  accountName: string;
};

type TOptionLabelMeta = { context: 'menu' | 'value' };

export interface SelectImportSiteProps {
  accountOptions: TSelectOption[];
  siteOptions: TSiteOption[];
  currentWorkspaceId: string;
  onSiteSelected: (site: TSiteOption) => void;
  currentSelectedAccount: string;
  selectedFieldCounts: Record<string, number>;
}

const countSelectedFieldsByAccount = (
  siteOptions: TSiteOption[],
  selectedFieldCounts: Record<string, number>,
): Record<string, number> =>
  siteOptions.reduce<Record<string, number>>((countsByAccount, site) => {
    const siteCount = selectedFieldCounts[site.value] ?? 0;

    if (siteCount === 0) {
      return countsByAccount;
    }

    return {
      ...countsByAccount,
      [site.accountId]: (countsByAccount[site.accountId] ?? 0) + siteCount,
    };
  }, {});

const SelectImportSite = ({
  accountOptions,
  siteOptions,
  currentWorkspaceId,
  onSiteSelected,
  currentSelectedAccount,
  selectedFieldCounts,
}: SelectImportSiteProps) => {
  const [selectedAccount, setSelectedAccount] = useState<string>(
    currentSelectedAccount ?? accountOptions[0]?.value,
  );

  const sites = siteOptions.filter((site) => site.accountId === selectedAccount);
  const selectedFieldCountsByAccount = countSelectedFieldsByAccount(
    siteOptions,
    selectedFieldCounts,
  );

  const onValueChange = (account: { items: TSelectOption[]; value: string[] }) => {
    setSelectedAccount(account.value[0]);
  };

  const formatAccountOption = (
    account: TSelectOption,
    { context }: TOptionLabelMeta,
  ): ReactElement | string => {
    const accountFieldCount = selectedFieldCountsByAccount[account.value] ?? 0;

    if (context !== 'menu' || accountFieldCount === 0) {
      return account.label;
    }

    return (
      <VStackRow width="full" alignItems="center" justifyContent="space-between">
        <VText truncate>{account.label}</VText>
        <VBadge colorPalette="gray">{accountFieldCount}</VBadge>
      </VStackRow>
    );
  };

  return (
    <VStack gap={6} pt={4}>
      <VFormField label={translate('select_account')} helperText="" invalid={false}>
        <VSelect
          collection={{ items: accountOptions }}
          value={selectedAccount}
          onValueChange={onValueChange}
          searchable={true}
          placeholder={translate('select_account')}
          multiple={false}
          groupBy=""
          name="account"
          menuPosition="fixed"
          formatOptionLabel={formatAccountOption}
        />
      </VFormField>

      {selectedAccount && (
        <VStack>
          <VText fontWeight="semibold">{translate('sites')}</VText>
          {sites.length === 0 && <VText>{translate('no_sites_found')}</VText>}

          {sites.map((site) => {
            const isCurrentSite = site.value === currentWorkspaceId;
            const hasNoCustomFields = site.customFields.length === 0;

            const isDisabled = isCurrentSite || hasNoCustomFields;
            const selectedFieldCount = selectedFieldCounts[site.value] ?? 0;

            const card = (
              <VCard
                size="sm"
                key={site.value}
                onClick={isDisabled ? undefined : () => onSiteSelected(site)}
                {...(isDisabled ? CURRENT_SITE_STYLE : SELECTABLE_SITE_STYLE)}
              >
                <VStackRow maxW="580px" alignItems={'center'} justifyContent="space-between">
                  <VText truncate>{site.label}</VText>
                  <VStackRow alignItems={'center'}>
                    {selectedFieldCount > 0 && (
                      <VBadge data-testid="selected-field-count">{selectedFieldCount}</VBadge>
                    )}
                    <VMaterialIcon name="chevronRight" />
                  </VStackRow>
                </VStackRow>
              </VCard>
            );

            if (!isDisabled) {
              return card;
            }

            return (
              <VTooltip
                key={site.value}
                content={
                  isCurrentSite
                    ? translate('site_currently_on')
                    : translate('no_custom_fields_available')
                }
                contentProps={undefined}
                disabled={undefined}
              >
                <Box data-testid="current-site">{card}</Box>
              </VTooltip>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

export default SelectImportSite;
